import axios from "axios";
import moment from "moment";
import _ from "lodash";
import { GetAllLedgersAPI } from "../config/api";
import jsPDF from "jspdf";
import { ledgerTemplate, LEDGER_PDF_OPTIONS } from "../assets/html/ledgerTemplate.js";

/**
 * Reduce for backBalance (transactions before start date) - same logic as Ledger.js
 */
function reduceBackBalance(sum, record) {
  if (record.type === "credit" && record.activityType === "sale" && record.transactionType === "challan") {
    return sum - Number(record.amount);
  }
  if (record.type === "credit" && record.activityType === "purchase" && record.transactionType === "challan") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "sale" && record.transactionType === "challan") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "purchase" && record.transactionType === "challan") {
    return sum - Number(record.amount);
  }
  if (record.type === "credit" && record.activityType === "sale" && record.transactionType === "bill") {
    return sum - Number(record.amount);
  }
  if (record.type === "credit" && record.activityType === "purchase" && record.transactionType === "bill") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "sale" && record.transactionType === "bill") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "purchase" && record.transactionType === "bill") {
    return sum - Number(record.amount);
  }
  return sum;
}

/**
 * Reduce for ongoingBalance (transactions in date range) - same logic as Ledger.js
 */
function reduceOngoingBalance(sum, record) {
  if (record.type === "credit" && record.activityType === "sale" && record.transactionType === "challan") {
    return sum - Number(record.amount);
  }
  if (record.type === "credit" && record.activityType === "purchase" && record.transactionType === "challan") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "sale" && record.transactionType === "challan") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "purchase" && record.transactionType === "challan") {
    return sum - Number(record.amount);
  }
  if (record.type === "credit" && record.activityType === "sale" && record.transactionType === "bill") {
    return sum - Number(record.amount);
  }
  if (record.type === "credit" && record.activityType === "purchase" && record.transactionType === "bill") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "sale" && record.transactionType === "bill") {
    return sum + Number(record.amount);
  }
  if (record.type === "debit" && record.activityType === "purchase" && record.transactionType === "bill") {
    return sum - Number(record.amount);
  }
  return sum;
}

/**
 * Fetch ledgers for an account (same query and balance logic as Ledger.js fetchLedgerData).
 * Fetches all ledgers without date params, then filters client-side and computes backBalance/ongoingBalance/tempBalance.
 */
const getLedgersForAccount = async (
  account,
  startDate,
  endDate,
  ledgerType,
  entity,
  authToken
) => {
  try {
    const effectiveLedgerType = ledgerType || "Challan";
    let query;
    if (effectiveLedgerType === "Challan") {
      query = `&accountId=${account._id}&transactionType=challan&transactionType=credit&transactionType=debit`;
    } else if (effectiveLedgerType === "Bill") {
      query = `&accountId=${account._id}&transactionType=bill&transactionType=credit&transactionType=debit`;
    } 
    
    // else {
    //   query = `&accountId=${account._id}&transactionType=challan&transactionType=credit&transactionType=debit`;
    // }

    const res = await axios.get(GetAllLedgersAPI(entity) + query, {
      headers: { Authorization: `Bearer ${authToken}` },
    });

    const allLedgers = res.data.ledgers || [];

    if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) {
      return {
        ledgers: [],
        openingBalance: 0,
        closingBalance: 0,
      };
    }

    const filteredRecords = allLedgers.filter((record) =>
      moment(record.date).startOf("day").isBefore(moment(startDate).startOf("day"))
    );

    let backBalance = filteredRecords.reduce(reduceBackBalance, 0);
    const currentOpeningChallanBalance = Number(account.openingChallanBalance || 0);
    const currentOpeningBillBalance = Number(account.openingBillBalance || 0);
    if (effectiveLedgerType === "Challan") {
      backBalance = backBalance + currentOpeningChallanBalance;
    } else if (effectiveLedgerType === "Bill") {
      backBalance = backBalance + currentOpeningBillBalance;
    }

    let filteredRecords2 = allLedgers.filter((record) =>
      moment(record.date).isBetween(
        moment(startDate).startOf("day"),
        moment(endDate).endOf("day"),
        null,
        "[]"
      )
    );

    // If Bill ledgers need tax, ask backend to enrich only the range rows.
    if (effectiveLedgerType === "Bill") {
      try {
        const sd = moment(startDate).format("DD-MM-YYYY");
        const ed = moment(endDate).format("DD-MM-YYYY");
        // Backend contract: transactionType=bill & includeBillTax=true (don't mix bill with credit/debit values)
        const rangeQuery = `&accountId=${account._id}&transactionType=bill&startDate=${sd}&endDate=${ed}&includeBillTax=true&limit=10000&offset=0`;
        const rangeRes = await axios.get(GetAllLedgersAPI(entity) + rangeQuery, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const enriched = rangeRes.data?.ledgers || [];
        if (Array.isArray(enriched) && enriched.length > 0) {
          filteredRecords2 = enriched;
        }
      } catch (_) {
        // Fall back to non-enriched range rows if backend call fails
      }
    }

    const ongoingBalance = filteredRecords2.reduce(reduceOngoingBalance, 0);
    const tempBalance = Number(backBalance) + Number(ongoingBalance);
    const ledgers = _.sortBy(filteredRecords2, (obj) => obj.date);

    return {
      ledgers,
      openingBalance: backBalance,
      closingBalance: tempBalance,
    };
  } catch (err) {
    console.error(
      `Error fetching ledgers for ${account.name || account.accountName}:`,
      err
    );
    return { ledgers: [], openingBalance: 0, closingBalance: 0 };
  }
};

/**
 * Generate ledger PDF blob (same data shape as LedgerList _generateLedger)
 */
const generateLedgerPDFBlob = (ledgerData) => {
  return ledgerTemplate(ledgerData);
};

/**
 * Download PDF blob with filename
 */
const downloadPDF = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Generate Ledger PDFs for multiple companies (same pattern as Bulk P2P Challan).
 * Pass account objects from the bulk modal so ledger fetch matches row-click Ledger.
 *
 * @param {Array<{_id: string, name?: string, accountName?: string, partyType?: string, openingChallanBalance?: number, openingPurchaseBalance?: number}>} accounts - Array of account objects from the bulk modal
 * @param {Date} startDate - Start date for the date range
 * @param {Date} endDate - End date for the date range
 * @param {string} ledgerType - "Challan" or "Bill"
 * @param {string} entity - Entity ID
 * @param {string} authToken - Authentication token
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} - { results, total, success }
 */
export async function generateLedgersForCompanies(
  accounts,
  startDate,
  endDate,
  ledgerType,
  entity,
  authToken,
  onProgress
) {
  if (!accounts || accounts.length === 0) {
    throw new Error("No accounts provided");
  }

  const effectiveLedgerType = ledgerType || "Challan";
  const results = [];
  const combinedDoc = new jsPDF(LEDGER_PDF_OPTIONS);
  let hasAnySection = false;

  for (let i = 0; i < accounts.length; i++) {
    const account = accounts[i];
    const companyName = account.name || account.accountName || "";

    try {
      const { ledgers, openingBalance, closingBalance } = await getLedgersForAccount(
        account,
        startDate,
        endDate,
        effectiveLedgerType,
        entity,
        authToken
      );

      if (!ledgers || ledgers.length === 0) {
        results.push({
          company: companyName,
          account,
          filename: null,
          ledgerCount: 0,
          status: "skipped",
        });
        if (onProgress) {
          onProgress({
            company: companyName,
            status: "skipped",
            reason: "No ledger entries found",
            index: i + 1,
            total: accounts.length,
            ledgerCount: 0,
          });
        }
        continue;
      }

      const ledgerTypeForPdf =
        ledgers.length > 0
          ? ledgers[0].transactionType || effectiveLedgerType
          : effectiveLedgerType;

      const htmlData = {
        ledgers,
        ledgerType: ledgerTypeForPdf,
        startDate,
        endDate,
        clientName: companyName,
         // Use either account.address or account.accountAddress if available
        clientAddress: account.address || account.accountAddress || "",
        openingBalance: openingBalance ?? 0,
        closingBalance: closingBalance ?? 0,
      };

      if (hasAnySection) {
        combinedDoc.addPage();
      }
      hasAnySection = true;
      const sectionStartPage = combinedDoc.internal.getNumberOfPages();
      ledgerTemplate(htmlData, { doc: combinedDoc, sectionStartPage });

      results.push({
        company: companyName,
        account,
        filename: null,
        ledgerCount: ledgers.length,
        status: "success",
      });

      if (onProgress) {
        onProgress({
          company: companyName,
          status: "completed",
          index: i + 1,
          total: accounts.length,
          ledgerCount: ledgers.length,
        });
      }

      if (i < accounts.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error(`Error generating ledger PDF for ${companyName}:`, error);
      if (onProgress) {
        onProgress({
          company: companyName,
          status: "error",
          error: error.message,
          index: i + 1,
          total: accounts.length,
        });
      }
    }
  }

  if (hasAnySection) {
    const dateRange =
      startDate && endDate && moment(startDate).isValid() && moment(endDate).isValid()
        ? `${moment(startDate).format("YYYY-MM-DD")}_${moment(endDate).format("YYYY-MM-DD")}`
        : moment().format("YYYY-MM-DD");
    const combinedFilename = `Bulk_Ledgers_${effectiveLedgerType}_${dateRange}.pdf`;
    const combinedBlob = combinedDoc.output("blob");
    downloadPDF(combinedBlob, combinedFilename);
  }

  return {
    results,
    total: accounts.length,
    success: results.filter((r) => r.status === "success").length,
  };
}
